import type {
  AllJurisdictionsRemittanceReportRequestSchema,
  CountyRemittanceReportRequestSchema,
  GlobalRemittanceReportRequestSchema,
  JurisdictionRemittanceReportRequestSchema,
  OperatorTestReportRequestSchema,
} from "@/hera-api/client/types.gen";

export const USER_TYPE_CLAIM = "https://auth.heraregistry.com/user_type";
export const USER_JURISDICTION_CLAIM =
  "https://auth.heraregistry.com/government_jurisdiction_id";

/** Sent on registrant API requests when an operator is impersonating a registrant account. */
export const REGISTRANT_PORTAL_IMPERSONATE_ACCOUNT_ID_HEADER =
  "X-Impersonate-Registrant-Account-Id";

/** URL query param to deep-link into the registrant portal as a given account (UUID). */
export const REGISTRANT_PORTAL_IMPERSONATE_URL_PARAM = "impersonate";

/** Sent on inspection API requests when an operator is impersonating an inspection account. */
export const INSPECTION_PORTAL_IMPERSONATE_ACCOUNT_ID_HEADER =
  "X-Impersonate-Inspection-Account-Id";

/** Sent on inspection API requests when an operator is impersonating an inspection user. */
export const INSPECTION_PORTAL_IMPERSONATE_USER_ID_HEADER =
  "X-Impersonate-Inspection-User-Id";

/** URL query param to deep-link into the inspection portal as a given account (UUID). */
export const INSPECTION_PORTAL_IMPERSONATE_URL_PARAM = "impersonate";

/** URL query param to deep-link into the inspection portal as a specific user (UUID). */
export const INSPECTION_PORTAL_IMPERSONATE_USER_URL_PARAM = "impersonate_user";

/** Sent on OMT/AMS API requests when an operator is impersonating a party email. */
export const OMT_AMS_PORTAL_IMPERSONATE_EMAIL_HEADER =
  "X-Impersonate-Omt-Ams-Email";

/** URL query param to deep-link into the OMT/AMS portal as a party email. */
export const OMT_AMS_PORTAL_IMPERSONATE_URL_PARAM = "impersonate";

/** Sent on government API requests when an operator is impersonating a government user. */
export const GOVERNMENT_PORTAL_IMPERSONATE_USER_ID_HEADER =
  "X-Impersonate-Government-User-Id";

/** URL query param to deep-link into the government portal as a government user (UUID). */
export const GOVERNMENT_PORTAL_IMPERSONATE_URL_PARAM = "impersonate";

/** Sent on title API requests when an operator is impersonating a title account. */
export const TITLE_PORTAL_IMPERSONATE_ACCOUNT_ID_HEADER =
  "X-Impersonate-Title-Account-Id";

/** URL query param to deep-link into the title portal as a title account (UUID). */
export const TITLE_PORTAL_IMPERSONATE_URL_PARAM = "impersonate";

export enum UserType {
  GOVERNMENT = "government",
  OPERATOR = "operator",
  REGISTRANT = "registrant",
  OMT_AMS = "omt_ams",
  TITLE_REQUESTOR = "title_requestor",
}

export enum ConnectionName {
  GOVERNMENT = "Government-Portal",
  OPERATOR = "Hera-Admin-Portal",
  REGISTRANT = "Registrant-Portal",
  OMT_AMS = "OMT-AMS-Portal",
  TITLE_REQUESTOR = "Title-Request-Portal",
}

// Extract report types from the actual schema types for type safety
type ExtractReportType<T> = T extends { report_type: infer R } ? R : never;

export type ReportTypes =
  | ExtractReportType<GlobalRemittanceReportRequestSchema>
  | ExtractReportType<JurisdictionRemittanceReportRequestSchema>
  | ExtractReportType<AllJurisdictionsRemittanceReportRequestSchema>
  | ExtractReportType<CountyRemittanceReportRequestSchema>
  | ExtractReportType<OperatorTestReportRequestSchema>;

// Typesafe constants that must match the schema types
export const ReportType = {
  GLOBAL_REMITTANCE_REPORT: "global_remittance_report",
  JURISDICTION_REMITTANCE_REPORT: "jurisdiction_remittance_report",
  ALL_JURISDICTIONS_REMITTANCE_REPORT: "all_jurisdictions_remittance_report",
  COUNTY_REMITTANCE_REPORT: "county_remittance_report",
  OPERATOR_TEST_REPORT: "operator_test_report",
} as const satisfies Record<string, ReportTypes>;
